import type { Logger } from "@expert/logging";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import type { TrainingTask } from "@/sdk";

import { useVoiceExpertAssistSessions } from "@/timeline/state/subscriptions/useVoiceExpertAssistSessions";

import { VoiceWorkspaceContentLegacy } from "./VoiceWorkspaceContentLegacy";
import { VoiceWorkspaceContentRedesigned } from "./VoiceWorkspaceContentRedesigned";
import { VoiceWorkspaceContentRedesignedV2 } from "./VoiceWorkspaceContentRedesignedV2";

type VoiceWorkspaceContentProps = {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    callControlsEnabled: boolean;
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasActiveVoiceTask: boolean;
    hasCallbackWaiting: boolean;
    isOutboundEnabled: boolean | undefined;
    isRealCustomerPanelEnabledForTraining: boolean | undefined;
    isVoiceWorkspaceRedesignEnabled: boolean;
    isVoiceWorkspaceRedesignV2Enabled: boolean;
    logger: Logger;
    sessionId: string;
    showExpertUniversity: undefined | boolean;
    showRealtimeTranscript: boolean;
    startedTimestamp: number;
};

/**
 * Each branch is a sibling that owns its own hook tree, so store-writing hooks (e.g.
 * `usePlatformGuideManager`) mount only for the active layout — mounting both causes competing
 * store writes that trigger render loops downstream.
 */
export function VoiceWorkspaceContent(props: VoiceWorkspaceContentProps) {
    // Mounted here (always-mounted for the whole session) rather than inside the timeline (PlatformTimeline),
    // which v2 only mounts on-call — subscribing that late races feature-flag resolution and drops transcripts.
    useVoiceExpertAssistSessions(props.logger);

    if (props.isVoiceWorkspaceRedesignV2Enabled) {
        return (
            <VoiceWorkspaceContentRedesignedV2
                activeExpertUniversityTask={props.activeExpertUniversityTask}
                customerPanelEnabled={props.customerPanelEnabled}
                customerSession={props.customerSession}
                hasActiveVoiceTask={props.hasActiveVoiceTask}
                logger={props.logger}
                sessionId={props.sessionId}
                showExpertUniversity={props.showExpertUniversity}
                startedTimestamp={props.startedTimestamp}
            />
        );
    }

    if (props.isVoiceWorkspaceRedesignEnabled) {
        return (
            <VoiceWorkspaceContentRedesigned
                activeExpertUniversityTask={props.activeExpertUniversityTask}
                activeTrainingTask={props.activeTrainingTask}
                customerPanelEnabled={props.customerPanelEnabled}
                customerSession={props.customerSession}
                hasActiveVoiceTask={props.hasActiveVoiceTask}
                logger={props.logger}
                sessionId={props.sessionId}
                showExpertUniversity={props.showExpertUniversity}
                startedTimestamp={props.startedTimestamp}
            />
        );
    }

    return <VoiceWorkspaceContentLegacy {...props} />;
}
